/**
 * Technology Icon Renderer - Technology node icon
 *
 * Renders a globe icon used for representing technology nodes
 * in the graph visualization with optional labels.
 */

import { getIconConfig } from '@/components/icons/nodeIcons/config/iconConfig';
import { getThemeColors } from '@/components/icons/nodeIcons/config/themeColors';
import {
  calculateIconPosition,
  drawCircleBackground,
  drawLabel,
  setupCanvas,
} from '@/components/icons/nodeIcons/renderers/base';

/**
 * Draw globe icon on canvas
 *
 * Internal helper function that draws a globe/network icon with
 * latitude and longitude lines.
 *
 * @param ctx - Canvas rendering context
 * @param x - X coordinate for globe
 * @param y - Y coordinate for globe
 * @param size - Size of the globe
 * @param color - Color for globe lines
 */
const drawGlobe = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  color: string
): void => {
  const scale = size / 24;
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(scale, scale);
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  // Draw outer circle
  ctx.beginPath();
  ctx.arc(12, 12, 9, 0, 2 * Math.PI);
  ctx.stroke();

  // Bottom right arc
  ctx.beginPath();
  ctx.moveTo(12, 21);
  ctx.arc(12, 12, 9, Math.PI / 2, -Math.PI / 6, false);
  ctx.stroke();

  // Bottom left arc
  ctx.beginPath();
  ctx.moveTo(12, 21);
  ctx.arc(12, 12, 9, Math.PI / 2, (7 * Math.PI) / 6, true);
  ctx.stroke();

  // Right meridian
  ctx.beginPath();
  ctx.moveTo(12, 21);
  ctx.bezierCurveTo(14.485, 21, 16.5, 16.97, 16.5, 12);
  ctx.bezierCurveTo(16.5, 7.03, 14.485, 3, 12, 3);
  ctx.stroke();

  // Left meridian
  ctx.beginPath();
  ctx.moveTo(12, 21);
  ctx.bezierCurveTo(9.515, 21, 7.5, 16.97, 7.5, 12);
  ctx.bezierCurveTo(7.5, 7.03, 9.515, 3, 12, 3);
  ctx.stroke();

  // Top right arc
  ctx.beginPath();
  ctx.moveTo(12, 3);
  ctx.arc(12, 12, 9, -Math.PI / 2, -Math.PI / 6, false);
  ctx.stroke();

  // Top left arc
  ctx.beginPath();
  ctx.moveTo(12, 3);
  ctx.arc(12, 12, 9, -Math.PI / 2, (-5 * Math.PI) / 6, true);
  ctx.stroke();

  // First latitude line
  ctx.beginPath();
  ctx.moveTo(19.843, 7.582);
  ctx.bezierCurveTo(16, 10.5, 8, 10.5, 4.157, 7.582);
  ctx.stroke();

  // Second latitude line
  ctx.beginPath();
  ctx.moveTo(20.716, 14.253);
  ctx.bezierCurveTo(16.5, 16.5, 7.5, 16.5, 3.284, 14.253);
  ctx.stroke();

  ctx.restore();
};

/**
 * Create technology icon data URL
 *
 * Generates a globe icon with circular background for use as
 * a technology node image in the graph visualization.
 *
 * @param currentTheme - Current theme ('light' or 'dark')
 * @param isHighlighted - Whether the icon should appear highlighted
 * @param label - Optional label text to display below icon
 * @param borderColorOverride - Optional border color override
 * @returns Data URL string of the rendered icon
 */
export const createTechnologyIconDataURL = (
  currentTheme: 'light' | 'dark',
  isHighlighted: boolean = false,
  label?: string,
  borderColorOverride?: string
): string => {
  const config = getIconConfig('technology');
  const colors = getThemeColors('technology', currentTheme, isHighlighted);
  const { canvas, ctx, size, centerX, centerY } = setupCanvas(config);

  // Draw circle background and border
  drawCircleBackground(
    ctx,
    centerX,
    centerY,
    size,
    config.scale,
    colors,
    borderColorOverride
  );

  // Calculate icon position
  const {
    x: globeX,
    y: globeY,
    scale: globeScale,
  } = calculateIconPosition(centerX, centerY, size, config, label);

  // Draw globe icon
  drawGlobe(ctx, globeX, globeY, config.iconWidth * globeScale, colors.icon);

  // Add the technology label if provided
  if (label) {
    drawLabel(ctx, centerX, centerY, size, config, label);
  }

  // Convert to data URL with higher quality
  return canvas.toDataURL('image/png', 1.0);
};
